import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import safeGet from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'

import Hub from 'base/hub'
import Event from 'base/event'
import { get } from 'base/lib/network'
import apiUrl from 'base/api_url'
import { requiresGDPRConsent } from 'base/lib/config'
import { getErrorsForAuthData, getErrorsForUserAccountData } from 'base/validators'
import { FormTypes } from 'base/constants/login'
import SignInForm from 'desktop/components/forms/sign_in_form'
import SignUpForm from 'desktop/components/forms/sign_up_form'
import MagicLinkSignIn from 'base/components/forms/magic_link_sign_in'
import MagicLinkSignInWithPassword from 'base/components/forms/magic_link_sign_in_with_password'
import EmailForm from 'base/components/forms/email_form'
import RouterLink from 'base/components/router_link'
import GoogleConnectButton from 'base/react/components/google_connect_button'
import { signIn, createAccount } from 'base/actions/asynchronous'
import routeTo from 'base/lib/route_to'
import TermsOfService from 'base/components/sign_up/terms_of_service'
import PasswordReset from 'base/containers/password_reset/password_reset'

class Login extends Component {
  static propTypes = {
    visitor: PropTypes.object.isRequired,
    signIn: PropTypes.func.isRequired,
    createAccount: PropTypes.func.isRequired,
    checkEmailAvailability: PropTypes.func.isRequired,
    onEmailChecked: PropTypes.func,
    onSignInSuccess: PropTypes.func,
    onSignUpSuccess: PropTypes.func,
    onSwitchToSignUp: PropTypes.func,
    onSwitchToSignIn: PropTypes.func,
    siteLocation: PropTypes.string,
    showExistingUserLink: PropTypes.bool,
    skipEmailEntry: PropTypes.bool,
    isNewUser: PropTypes.bool,
    signInOnly: PropTypes.bool,
    googleOnly: PropTypes.bool,
    hasRepeatUserCookie: PropTypes.bool,
    email: PropTypes.string,
    emailFormContent: PropTypes.node,
    emailOptin: PropTypes.bool,
  }

  static defaultProps = {
    showExistingUserLink: false,
    skipEmailEntry: false,
    email: null,
    emailOptin: false,
  }

  checkNewUser = props => {
    if (props.hasRepeatUserCookie) {
      return false
    } else if (props.isNewUser || !isUndefined(props.isNewUser)) {
      return props.isNewUser
    } else if (props.skipEmailEntry) {
      return false
    } else {
      return undefined
    }
  }

  state = {
    email: safeGet(this.props, 'email', ''),
    password: '',
    isPasswordlessUser: false,
    emailAvailable: true,
    isLoading: false,
    isNewUser: this.checkNewUser(this.props),
    serverErrorMessage: '',
    errorsForCheckEmail: {},
    errorsForSignInForm: {},
    errorsForSignUpForm: {},
    signInData: null,
    formType: FormTypes.EMAIL,
  }

  componentDidMount() {
    if (this.props.email) {
      get(apiUrl('users/availability'), {
        email: this.props.email,
        email_optin: this.props.emailOptin,
        site_location: this.props.siteLocation,
      })
      this.handleCheckEmail(this.props.email)
    }
  }

  handleCheckEmail = async email => {
    Hub.pub(Event.Login.EMAIL_ENTERED, {
      site_location: this.props.siteLocation,
    })

    const errors = getErrorsForUserAccountData({ email: email })

    if (!isEmpty(errors)) {
      this.setState({ errorsForCheckEmail: errors })
      return
    }

    this.setState({ isLoading: true, email })

    this.props
      .checkEmailAvailability({
        email,
        email_optin: this.props.emailOptin,
        site_location: this.props.siteLocation,
      })
      .then(({ is_available, has_password }) => {
        this.setState({
          isNewUser: is_available,
          serverErrorMessage: null,
          isPasswordlessUser: !is_available && !has_password,
          emailAvailable: is_available,
          formType: is_available ? FormTypes.SIGN_UP : FormTypes.SIGN_IN,
        })

        if (this.props.onEmailChecked) {
          this.props.onEmailChecked({
            isNewUser: is_available,
            isPasswordlessUser: !is_available && !has_password,
          })
        }

        if (is_available) {
          Hub.pub(Event.Login.SHOW_SIGN_UP_VIEW, {
            site_location: this.props.siteLocation,
          })
        } else {
          Hub.pub(Event.Login.SHOW_SIGN_IN_VIEW, {
            site_location: this.props.siteLocation,
          })
        }
      })
      .fail(this.handleServerError)
      .always(() => this.setState({ isLoading: false }))
  }

  handleSignUp = user => {
    const errors = getErrorsForAuthData(user)

    if (!isEmpty(errors)) {
      this.setState({ errorsForSignUpForm: errors })
      return
    }

    this.setState({ isLoading: true })
    this.props
      .createAccount(user, this.props.siteLocation)
      .then(() => {
        if (this.props.onSignUpSuccess) {
          this.props.onSignUpSuccess()
        }
      })
      .fail(this.handleServerError)
      .always(() => this.setState({ isLoading: false }))
  }

  handleSignIn = async data => {
    const errors = getErrorsForAuthData(data)

    if (!isEmpty(errors)) {
      this.setState({ errorsForSignInForm: errors })
      return
    }

    this.setState({ isLoading: true })

    this.props
      .signIn(data, this.props.siteLocation)
      .then(() => {
        if (this.props.onSignInSuccess) {
          this.props.onSignInSuccess()
        }
      })
      .catch(({ responseJSON }) => {
        if (window.turnstile) {
          window.turnstile.reset()
        }

        if (responseJSON && responseJSON.code === 'ACCOUNT_DOES_NOT_EXIST') {
          this.handleSwitchToSignUp()
        } else {
          this.handleServerError({ responseJSON })
        }
      })
      .finally(() => this.setState({ isLoading: false }))
  }

  handleServerError = response => {
    const errorMessage = response && response.responseJSON && response.responseJSON.message

    this.setState({
      serverErrorMessage: errorMessage || 'An unknown error occurred',
    })
  }

  handleSwitchToSignUp = () => {
    const isNewUser = true

    this.setState({
      isNewUser,
      serverErrorMessage: null,
      formType: FormTypes.SIGN_UP,
    })

    if (this.props.onSwitchToSignUp) {
      this.props.onSwitchToSignUp({ isNewUser })
    }

    if (this.props.onEmailChecked) {
      this.props.onEmailChecked({ isNewUser })
    }

    Hub.pub(Event.Login.SWITCH_TO_SIGN_UP, {
      site_location: this.props.siteLocation,
    })
  }

  handleSwitchToSignIn = () => {
    const isNewUser = false

    this.setState({
      isNewUser,
      serverErrorMessage: null,
      formType: FormTypes.SIGN_IN,
    })

    if (this.props.onSwitchToSignIn) {
      this.props.onSwitchToSignIn({ isNewUser })
    }

    if (this.props.onEmailChecked) {
      this.props.onEmailChecked({ isNewUser })
    }

    Hub.pub(Event.Login.SWITCH_TO_SIGN_IN, {
      site_location: this.props.siteLocation,
    })
  }

  handleClickCreateAccount = () => {
    const isNewUser = true
    this.setState({ isNewUser })
    if (this.props.onSwitchToSignUp) {
      this.props.onSwitchToSignUp({ isNewUser })
    }
    Hub.pub(Event.Login.SWITCH_TO_SIGN_IN, {
      site_location: this.props.siteLocation,
    })
  }

  renderAppropriateForm() {
    if (this.state.isNewUser) {
      return this.renderSignUpForm()
    } else if (this.state.formType === FormTypes.MAGIC_LINK_PASSWORD) {
      return this.renderMagicLinkSignInWithPassword()
    } else if (this.state.formType === FormTypes.MAGIC_LINK_PASSWORDLESS) {
      return this.renderMagicLinkSignIn()
    } else if (!this.state.emailAvailable && this.state.isPasswordlessUser) {
      return this.renderPasswordReset()
    } else if (
      !this.state.emailAvailable ||
      this.props.hasRepeatUserCookie ||
      this.state.formType === FormTypes.SIGN_IN ||
      this.props.isSigningIn
    ) {
      return this.renderSignInForm()
    } else if (this.state.formType === FormTypes.SIGN_UP) {
      return this.renderSignUpForm()
    } else {
      return this.renderSignUpForm()
    }
  }

  renderMagicLinkSignInWithPassword() {
    return (
      <MagicLinkSignInWithPassword
        initialEmail={this.state.email}
        errors={this.state.errorsForSignInForm}
        siteLocation={this.props.siteLocation}
        onClickSwitchToPasswordless={() =>
          this.setState({ formType: FormTypes.MAGIC_LINK_PASSWORDLESS })
        }
        onSubmit={this.handleSignIn}
        isLoading={this.state.isLoading}
      />
    )
  }

  renderMagicLinkSignIn() {
    return (
      <MagicLinkSignIn
        initialEmail={this.state.email}
        siteLocation={this.props.siteLocation}
        isPasswordlessUser={this.state.isPasswordlessUser}
        onServerError={this.handleServerError}
        onClickSwitchToPassword={() => this.setState({ formType: FormTypes.MAGIC_LINK_PASSWORD })}
      />
    )
  }

  renderSignInForm() {
    const initialEmail = this.props.hasRepeatUserCookie
      ? this.props.visitor.email
      : this.state.email

    return (
      <div>
        <SignInForm
          isLoading={this.state.isLoading}
          initialEmail={initialEmail}
          errors={this.state.errorsForSignInForm}
          onSubmit={this.handleSignIn}
        />
        <div className="login__social-buttons">{this.renderSocialButtons()}</div>
        {!this.props.signInOnly && (
          <div className="login__user-link">
            <span className="login__footer-signup-text">
              Don't have an account?{' '}
              <RouterLink className="login__footer-signup-link" onClick={this.handleSwitchToSignUp}>
                Sign up
              </RouterLink>
              <Fragment>
                {' | '}
                <RouterLink
                  className="login__footer-signup-link"
                  onClick={() => routeTo('/reset', { hard: true })}
                >
                  Reset password
                </RouterLink>
              </Fragment>
            </span>
          </div>
        )}
        {this.props.signInOnly && (
          <div className="login__user-link">
            <RouterLink
              className="login__footer-signup-link"
              onClick={() => routeTo('/reset', { hard: true })}
            >
              Reset password
            </RouterLink>
          </div>
        )}
      </div>
    )
  }

  renderSignUpForm() {
    return (
      <div>
        <SignUpForm
          isLoading={this.state.isLoading}
          initialEmail={this.state.email}
          errors={this.state.errorsForSignUpForm}
          onSubmit={this.handleSignUp}
          siteLocation={this.props.siteLocation}
          password={this.state.password}
          defaultOptIn={!requiresGDPRConsent()}
        />
      </div>
    )
  }

  renderPasswordReset() {
    return (
      <PasswordReset email={this.state.email} isPasswordlessUser={this.state.isPasswordlessUser} />
    )
  }

  renderExistingUserLink() {
    return (
      <RouterLink className="login__footer-link" onClick={this.handleSwitchToSignIn}>
        Already have an account? <span className="login__footer-link--underlined">Sign in</span>
      </RouterLink>
    )
  }

  renderSocialButtons() {
    return (
      <div className="login__social">
        <p className="login__social-message">Or continue with</p>
        <div className="login__social-button-wrapper">
          <GoogleConnectButton
            showIcon
            onSuccess={this.props.onSignInSuccess}
            onError={this.handleServerError}
            siteLocation={this.props.siteLocation}
            className="login__social-button"
          />
        </div>
      </div>
    )
  }

  renderGoogleOnly() {
    return (
      <div className="login__google-only" style={{ textAlign: 'center' }}>
        <div className="login__social-button-wrapper" style={{ justifyContent: 'center' }}>
          <GoogleConnectButton
            showIcon
            onSuccess={this.props.onSignInSuccess}
            onError={this.handleServerError}
            siteLocation={this.props.siteLocation}
            className="login__social-button"
          />
        </div>
      </div>
    )
  }

  render() {
    if (this.props.googleOnly) {
      return (
        <div className="login-v2" style={{ flex: '0 0 auto', justifyContent: 'center' }}>
          <div>
            {this.state.serverErrorMessage && (
              <div className="login__error-message">{this.state.serverErrorMessage}</div>
            )}
            <div className="login__content-container">{this.renderGoogleOnly()}</div>
          </div>
        </div>
      )
    }

    const content = (() => {
      if (isUndefined(this.state.isNewUser)) {
        return (
          <EmailForm
            errors={this.state.errorsForCheckEmail}
            isLoading={this.state.isLoading}
            onSubmit={this.handleCheckEmail}
            buttonText={isUndefined(this.props.isNewUser) ? 'Sign Up' : 'Log In'}
          />
        )
      }

      return this.renderAppropriateForm()
    })()

    return (
      <div className="login-v2">
        <div>
          {this.state.serverErrorMessage && (
            <div className="login__error-message">{this.state.serverErrorMessage}</div>
          )}

          <div className="login__content-container">{content}</div>
          {this.state.formType === FormTypes.EMAIL && this.props.emailFormContent}
          {this.props.showExistingUserLink || this.state.isNewUser ? (
            <div className="login__user-link">{this.renderExistingUserLink()}</div>
          ) : null}
        </div>

        {(this.props.skipEmailEntry ||
          isUndefined(this.state.isNewUser) ||
          this.state.isNewUser) && (
          <TermsOfService noLineBreak showWelcomeOfferDisclaimer={this.props.emailOptin} />
        )}
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    visitor: state.session.visitor,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    signIn: (data, siteLocation) => dispatch(signIn(data, { siteLocation })),
    createAccount: (userData, siteLocation) => dispatch(createAccount(userData, { siteLocation })),
    checkEmailAvailability: authData => get(apiUrl('users/availability'), authData),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
